import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../App';
import { CreditCard, CheckCircle, Zap, Shield, Star, ArrowRight, Clock } from 'lucide-react';

const LOGO = 'https://customer-assets.emergentagent.com/job_cc5301f8-029c-4cb1-afaa-ab22368334bb/artifacts/yofpexsn_Logo%20-%20official%20300x300.png';

const DEFAULT_PLANS = [
  { id: 'starter', name: 'Starter', price: 49, agents: 2, color: '#2E7D32', features: ['2 AI Employees', '100 messages/month', 'Document upload & RAG', 'Basic reports', 'Email support'] },
  { id: 'professional', name: 'Professional', price: 149, agents: 6, color: '#7B1FA2', popular: true, features: ['6 AI Employees', '1,000 messages/month', 'HRIS integrations', 'Advanced reports & export', 'Template library', 'Priority support'] },
  { id: 'enterprise', name: 'Enterprise', price: 399, agents: 11, color: '#F9A825', features: ['All 11 AI Employees', 'Unlimited messages', 'SSO/OIDC integration', 'Custom prompt templates', 'API access', 'Dedicated support'] },
];

export default function SubscriptionPaywall({ onComplete }) {
  const { user } = useAuth();
  const [plans, setPlans] = useState(DEFAULT_PLANS);
  const [selectedPlan, setSelectedPlan] = useState('professional');

  const [subscribing, setSubscribing] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [stripeConfigured, setStripeConfigured] = useState(false);

  useEffect(() => {
    api.get('/api/pricing').then(r => {
      if (r.data) {
        const updated = DEFAULT_PLANS.map(dp => {
          const serverPlan = r.data[dp.id];
          return serverPlan ? { ...dp, price: serverPlan.price, agents: serverPlan.agents, name: serverPlan.name } : dp;
        });
        setPlans(updated);
      }
    }).catch(e => console.error('Pricing load error:', e));
    api.get('/api/payments/config').then(r => { if (r.data?.configured) setStripeConfigured(true); }).catch(e => console.error('Stripe config error:', e));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- runs once on mount

  const handleStartTrial = async () => {
    setSubscribing(true); setError('');
    try {
      if (stripeConfigured) {
        // Redirect to Stripe Checkout with 14-day trial
        const res = await api.post('/api/payments/create-checkout', { plan: selectedPlan });
        if (res.data?.checkout_url) { window.location.href = res.data.checkout_url; return; }
      }
      // Fallback if Stripe redirect fails or not configured
      if (!cardNumber || !expiry || !cvc) { setError('Please fill in all card details'); setSubscribing(false); return; }
      await api.post('/api/subscription/start-trial', { plan: selectedPlan, card_provided: true });
      onComplete();
    } catch (e) { setError(e.response?.data?.detail || 'Something went wrong'); }
    finally { setSubscribing(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'white', borderRadius: 24, width: '100%', maxWidth: step === 1 ? 900 : 520, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #1B5E20, #4A148C)', padding: '32px 36px', borderRadius: '24px 24px 0 0', color: 'white', textAlign: 'center' }}>
          <img src={LOGO} alt="" style={{ width: 48, height: 48, borderRadius: 12, marginBottom: 12 }} />
          <h2 style={{ fontSize: 26, fontWeight: 800, marginBottom: 6 }}>Welcome to Panzix HR AI Workforce!</h2>
          <p style={{ opacity: 0.85, fontSize: 15 }}>{step === 1 ? 'Choose a plan to start your 14-day free trial' : 'Enter payment details for your trial'}</p>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12, background: 'rgba(255,255,255,0.2)', padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
            <Clock size={14} />14-day free trial on all plans
          </div>
        </div>

        <div style={{ padding: '28px 36px 36px' }}>
          {step === 1 && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
                {plans.map(plan => (
                  <div key={plan.id} onClick={() => setSelectedPlan(plan.id)}
                    style={{ border: `3px solid ${selectedPlan === plan.id ? plan.color : '#e2e8f0'}`, borderRadius: 16, padding: 24, cursor: 'pointer', position: 'relative', transition: 'all 0.2s', background: selectedPlan === plan.id ? `${plan.color}08` : 'white', textAlign: 'center' }}
                    data-testid={`trial-plan-${plan.id}`}>
                    {plan.popular && <div style={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', background: plan.color, color: 'white', fontSize: 11, fontWeight: 700, padding: '3px 14px', borderRadius: 20 }}>MOST POPULAR</div>}
                    <h3 style={{ color: plan.color, fontSize: 18, fontWeight: 800, marginBottom: 4 }}>{plan.name}</h3>
                    <div style={{ fontSize: 36, fontWeight: 900, color: '#0f172a', margin: '8px 0' }}>${plan.price}<span style={{ fontSize: 14, fontWeight: 400, color: '#94a3b8' }}>/mo</span></div>
                    <p style={{ fontSize: 13, color: '#64748b', marginBottom: 14 }}>{plan.agents} AI Employees</p>
                    <div style={{ textAlign: 'left' }}>
                      {plan.features.map(f => <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13, color: '#374151' }}><CheckCircle size={14} color={plan.color} />{f}</div>)}
                    </div>
                  </div>
                ))}
              </div>
              <button className="btn-action" onClick={() => stripeConfigured ? handleStartTrial() : setStep(2)} disabled={subscribing} style={{ width: '100%', padding: '16px', fontSize: 16, borderRadius: 14 }} data-testid="continue-to-payment">
                {subscribing ? 'Redirecting to secure checkout...' : <>Continue with {plans.find(p => p.id === selectedPlan)?.name} <ArrowRight size={18} style={{ marginLeft: 8 }} /></>}
              </button>
              <p style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', marginTop: 12 }}>14-day free trial. Cancel anytime. No charges until trial ends.</p>
            </>
          )}

          {step === 2 && (
            <>
              <div style={{ background: '#f0fdf4', borderRadius: 12, padding: 16, marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div><span style={{ fontWeight: 700, fontSize: 16, color: '#166534' }}>{plans.find(p => p.id === selectedPlan)?.name} Plan</span>
                <span style={{ marginLeft: 10, color: '#64748b' }}>${plans.find(p => p.id === selectedPlan)?.price}/mo after trial</span></div>
                <button onClick={() => setStep(1)} style={{ background: 'none', border: 'none', color: 'var(--panzix-green)', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Change plan</button>
              </div>
              {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}
              <div className="form-group">
                <label><CreditCard size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />Card Number</label>
                <input data-testid="card-number" value={cardNumber} onChange={e => setCardNumber(e.target.value.replace(/\D/g, '').replace(/(\d{4})/g, '$1 ').trim())} placeholder="4242 4242 4242 4242" maxLength={19} />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div className="form-group" style={{ flex: 1 }}><label>Expiry</label><input data-testid="card-expiry" value={expiry} onChange={e => setExpiry(e.target.value)} placeholder="MM/YY" maxLength={5} /></div>
                <div className="form-group" style={{ flex: 1 }}><label>CVC</label><input data-testid="card-cvc" value={cvc} onChange={e => setCvc(e.target.value)} placeholder="123" maxLength={4} /></div>
              </div>
              <button className="btn-action" onClick={handleStartTrial} disabled={subscribing} style={{ width: '100%', padding: '16px', fontSize: 16, borderRadius: 14 }} data-testid="start-trial">
                {subscribing ? 'Processing...' : <><Zap size={18} style={{ marginRight: 8 }} />Start 14-Day Free Trial</>}
              </button>
              <p style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', marginTop: 12 }}>Your card will be charged ${plans.find(p => p.id === selectedPlan)?.price}/mo after the 14-day trial. Cancel anytime.</p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 16 }}>
                {['256-bit SSL', 'SOC2 Ready', 'Cancel Anytime'].map(t => <span key={t} style={{ fontSize: 11, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}><Shield size={10} />{t}</span>)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
